import {useMemo, useRef, useState} from "react";

// Editable list of phone numbers with duplicate / empty detection
export const usePhoneNumbers = () => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const nextId = useRef(0);

  const addNumbers = (values) => {
    if (!values.length) return;
    setPhoneNumbers(prev => [
      ...prev,
      ...values.map(value => ({id: nextId.current++, value}))
    ]);
  };

  const updateNumber = (id, value) => {
    setPhoneNumbers(prev => prev.map(row => row.id === id ? {...row, value} : row));
  };

  const deleteNumber = (id) => {
    setPhoneNumbers(prev => prev.filter(row => row.id !== id));
  };

  const removeDuplicates = () => {
    const seen = new Set();
    setPhoneNumbers(prev => prev.filter(row => {
      const key = row.value.trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }));
  };

  const clear = () => setPhoneNumbers([]);

  // How many times each (trimmed) number occurs
  const counts = useMemo(() => {
    const map = new Map();
    phoneNumbers.forEach(row => {
      const key = row.value.trim();
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [phoneNumbers]);

  const isEmpty = (row) => row.value.trim() === "";
  const isDuplicate = (row) => !isEmpty(row) && counts.get(row.value.trim()) > 1;
  const hasIssue = (row) => isDuplicate(row) || isEmpty(row);

  const duplicateCount = phoneNumbers.filter(isDuplicate).length;
  const emptyCount = phoneNumbers.filter(isEmpty).length;

  return {
    phoneNumbers,
    addNumbers,
    updateNumber,
    deleteNumber,
    removeDuplicates,
    clear,
    isDuplicate,
    isEmpty,
    hasIssue,
    duplicateCount,
    emptyCount,
    invalidCount: duplicateCount + emptyCount,
  };
};
